import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CreateCampaignModal from '../components/campaigns/CreateCampaignModal';
import PageHeader from '../components/ui/PageHeader';
import StatCard from '../components/ui/StatCard';
import Pill from '../components/ui/Pill';
import MiniBar from '../components/ui/MiniBar';
import ActionButton from '../components/ui/ActionButton';
import { dataFetch } from '../api';

const { FiPlus, FiUsers, FiAlertTriangle, FiSearch, FiChevronRight, FiLoader } = FiIcons;

const typeColor = {
  Mandatory:  'bg-slate-50 text-slate-600 border-slate-200',
  Remedial:   'bg-slate-50 text-slate-600 border-slate-200',
  Onboarding: 'bg-slate-50 text-slate-600 border-slate-200',
  Compliance: 'bg-slate-50 text-slate-600 border-slate-200',
};

const overdueMap = { TC001: 1, TC002: 0, TC003: 3, TC004: 1, TC005: 0 };

const getArray = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try { return JSON.parse(val); } catch { return []; }
};

const Campaigns = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [modulesList, setModulesList] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCampaigns = async () => {
    try {
      const res = await dataFetch('read/campaigns?limit=5000');
      if (res?.status === 'success') {
        const parsedCampaigns = (res.data || []).map(c => ({
          ...c,
          assignedLearners: getArray(c.assignedLearners),
          trainingContent: getArray(c.trainingContent)
        }));
        setCampaigns(parsedCampaigns);
      }
    } catch (error) {
      console.error('Failed to fetch campaigns', error);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await dataFetch('read/modules?limit=5000');
      if (res?.status === 'success') {
        setModulesList(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch modules', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const res = await dataFetch('read/assignments?limit=5000');
      if (res?.status === 'success') {
        setAssignments(res.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch assignments', error);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchCampaigns(), fetchModules(), fetchAssignments()]);
      setLoading(false);
    };
    loadAll();
  }, []);

  const getCampaignCompletion = (campaign, fallbackRate) => {
    const campaignAssignments = assignments.filter(a => String(a.campaign_id) === String(campaign.id));
    
    // Get unique assigned learners based on the explicit list and assignments
    const assignedIds = [...new Set([
      ...(campaign.assignedLearners || []).map(String),
      ...campaignAssignments.map(a => String(a.employee_id))
    ])];
    
    if (assignedIds.length === 0) return fallbackRate || 0;
    
    let completedLearners = 0;
    
    assignedIds.forEach(learnerId => {
      const learnerAssignments = campaignAssignments.filter(a => String(a.employee_id) === String(learnerId));
      if (learnerAssignments.length > 0 && learnerAssignments.every(a => a.status === 'Completed')) {
        completedLearners++;
      }
    });

    return Math.round((completedLearners / assignedIds.length) * 100);
  };

  const filtered = campaigns.filter(c =>
    (filter === 'All' || c.status === filter) &&
    (c.campaignName || '').toLowerCase().includes(search.toLowerCase())
  );

  const avg = campaigns.length > 0 
    ? Math.round(campaigns.reduce((sum, current) => sum + getCampaignCompletion(current, current.completionRate), 0) / campaigns.length) 
    : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <PageHeader
        eyebrow="Training"
        title="Campaigns"
        description="Create, manage and monitor all security awareness training campaigns."
        action={
          <ActionButton icon={FiPlus} label="New Campaign" onClick={() => setShowCreate(true)} />
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total" value={campaigns.length} valueColor="text-slate-900" />
        <StatCard label="Active" value={campaigns.filter(c => c.status === 'Active').length} valueColor="text-blue-600" />
        <StatCard label="Completed" value={campaigns.filter(c => c.status === 'Completed').length} valueColor="text-emerald-600" />
        <StatCard label="Avg. Completion" value={`${avg}%`} valueColor="text-slate-900" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex gap-1.5 bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
          {['All', 'Active', 'Completed', 'Draft'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === s ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {s}
            </button>
          ))}
        </div>
        <div className="relative">
          <SafeIcon icon={FiSearch} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search campaigns…"
            className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-slate-400 shadow-sm w-64" />
        </div>
      </div>

      <p className="text-sm text-slate-500">Showing <span className="font-semibold text-slate-700">{filtered.length}</span> of {campaigns.length} campaigns</p>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-400 font-bold bg-white">
                <th className="px-6 py-4">Campaign</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Learners</th>
                <th className="px-6 py-4">Completion</th>
                <th className="px-6 py-4">Due Date</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-sm">
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">
                    <SafeIcon icon={FiLoader} className="animate-spin text-2xl mx-auto mb-2" />
                    <p className="mt-2 text-sm">Loading campaigns...</p>
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-slate-500">
                    No campaigns found. Click "New Campaign" to get started.
                  </td>
                </tr>
              ) : filtered.map((c, i) => {
                const modules = (c.trainingContent || []).map(id => modulesList.find(m => String(m.id) === String(id))).filter(Boolean);
                const overdue = overdueMap[c.id] ?? 0;
                
                const compRate = getCampaignCompletion(c, c.completionRate);
                const barColor = compRate === 100 ? 'bg-emerald-500' : compRate >= 60 ? 'bg-blue-500' : 'bg-amber-400';

                return (
                  <tr key={c.id} onClick={() => navigate(`/campaigns/${c.id}`)}
                    className="hover:bg-slate-50 cursor-pointer transition-colors group bg-white">
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{c.campaignName}</p>
                      <p className="text-[11px] text-slate-400 mt-0.5">{modules.length} module{modules.length !== 1 ? 's' : ''}</p>
                      {overdue > 0 && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-600 mt-1">
                          <SafeIcon icon={FiAlertTriangle} className="text-[10px]" />{overdue} overdue
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border ${typeColor[c.campaignType] || typeColor.Mandatory}`}>{c.campaignType}</span>
                    </td>
                    <td className="px-6 py-4"><Pill status={c.status} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1.5 text-sm text-slate-600">
                        <SafeIcon icon={FiUsers} className="text-slate-400 text-xs" />
                        {(c.assignedLearners || []).length}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <MiniBar percent={compRate} colorClass={barColor} />
                        <span className="text-[11px] font-semibold text-slate-500">{compRate}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs text-slate-600">{c.endDate}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <SafeIcon icon={FiChevronRight} className="text-slate-300 group-hover:text-slate-600 transition-colors" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && (
          <div className="px-6 py-3 border-t border-slate-100 text-xs text-slate-400 font-medium">
            {filtered.length} campaigns
          </div>
        )}
      </div>

      {showCreate && (
        <CreateCampaignModal onClose={() => setShowCreate(false)}
          onCreate={c => { setCampaigns(p => [...p, c]); setShowCreate(false); }} />
      )}
    </motion.div>
  );
};

export default Campaigns;